import {DatePicker} from '@ark-ui/react/date-picker';
import type {ChakraProps} from '@chakra-ui/react';
import {faCalendar} from '@fortawesome/pro-light-svg-icons/faCalendar';
import {faChevronLeft} from '@fortawesome/pro-light-svg-icons/faChevronLeft';
import {faChevronRight} from '@fortawesome/pro-light-svg-icons/faChevronRight';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {ChakraDiv} from '@linx-security/ui/utils/chakra-elements';
import type {ChangeEventHandler} from 'react';

import {calendarDateFormatter} from './helpers';

import {
  Box,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  SkeletonText,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  forwardRef,
  useControllableState,
} from '@chakra-ui/react';

type Props = {
  value?: string;
  onChange: (value: string) => void;
  onBlur?: ChangeEventHandler<HTMLInputElement>;
  defaultValue?: string;
  label?: string;
  isDisabled?: boolean;
  isLoaded?: boolean;
  size?: 'md' | 'lg';
  sx?: ChakraProps['sx'];
};

export const PopoverDatePicker = forwardRef<Props, 'input'>((props, ref) => {
  const {label, isDisabled, isLoaded = true, sx, size} = props;
  const [value = props.defaultValue, setValue] = useControllableState({
    value: props.value,
    onChange: props.onChange,
  });

  const handleValueChange: Parameters<typeof DatePicker.Root>[0]['onValueChange'] = ({value}) => {
    if (!value || value.length === 0) return;
    const dateValue = value[0];
    if (!dateValue) return;
    // Extract year, month, day directly from the DateValue object to avoid timezone issues
    // This ensures we always get the calendar date that was clicked, not a timezone-shifted value
    const year = dateValue.year;
    const month = String(dateValue.month).padStart(2, '0');
    const day = String(dateValue.day).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    setValue(dateString);
  };

  return (
    <DatePicker.Root
      value={value ? [value] : []}
      closeOnSelect
      aria-label={label}
      disabled={isDisabled || !isLoaded}
      onValueChange={handleValueChange}
      format={calendarDateFormatter}
    >
      <DatePicker.Control>
        <DatePicker.Trigger asChild>
          <InputGroup
            size={size}
            sx={{
              ...sx,
              height: `auto`,
            }}
          >
            <DatePicker.Input asChild>
              <Input
                ref={ref}
                sx={{
                  color: isLoaded ? undefined : `transparent`,
                }}
                onBlur={props.onBlur}
                placeholder={isLoaded ? `Select a date` : ``}
              />
            </DatePicker.Input>
            {!isLoaded && (
              <SkeletonText
                noOfLines={1}
                minW={`10ch`}
                position={`absolute`}
                left={`space-150`}
                top={`50%`}
                transform={`translateY(-50%)`}
              >
                --/--/--
              </SkeletonText>
            )}
            <InputRightElement
              sx={{
                visibility: isLoaded ? `visible` : `hidden`,
                width: `auto`,
                padding: `8px 12px`,
              }}
            >
              <FontAwesomeIcon icon={faCalendar} />
            </InputRightElement>
          </InputGroup>
        </DatePicker.Trigger>
      </DatePicker.Control>
      <DatePicker.Positioner>
        <DatePicker.Content asChild>
          <Box
            sx={{
              backgroundColor: `white`,
              borderRadius: `md`,
              border: `1px solid`,
              padding: `8`,
              borderColor: `border`,
              zIndex: 1500,
            }}
          >
            <DatePicker.View view={`day`}>
              <DatePicker.Context>
                {api => (
                  <>
                    <DatePicker.ViewControl asChild>
                      <Flex justifyContent={`space-between`} alignItems={`middle`}>
                        <DatePicker.PrevTrigger asChild>
                          <IconButton
                            aria-label={`Next month`}
                            variant={`unstyled`}
                            icon={<FontAwesomeIcon icon={faChevronLeft} />}
                            sx={{
                              margin: `2`,
                              padding: `4`,
                              height: `auto`,
                              minHeight: `16`,
                              width: `auto`,
                              minWidth: `16`,
                              fontSize: `sm`,
                              _hover: {
                                backgroundColor: `blackAlpha.100`,
                              },
                            }}
                          />
                        </DatePicker.PrevTrigger>

                        <Text
                          as={ChakraDiv}
                          sx={{
                            fontSize: `sm`,
                            fontWeight: `semibold`,
                            display: `flex`,
                            alignItems: `center`,
                            cursor: `default`,
                          }}
                        >
                          <DatePicker.RangeText />
                        </Text>

                        <DatePicker.NextTrigger asChild>
                          <IconButton
                            aria-label={`Next month`}
                            variant={`unstyled`}
                            icon={<FontAwesomeIcon icon={faChevronRight} />}
                            sx={{
                              margin: `2`,
                              padding: `4`,
                              height: `auto`,
                              minHeight: `16`,
                              width: `auto`,
                              minWidth: `16`,
                              fontSize: `sm`,
                              _hover: {
                                backgroundColor: `blackAlpha.100`,
                              },
                            }}
                          />
                        </DatePicker.NextTrigger>
                      </Flex>
                    </DatePicker.ViewControl>

                    <DatePicker.Table asChild>
                      <Table
                        sx={{
                          maxWidth: `234px`,
                          position: `relative`,
                          marginTop: `8`,
                        }}
                      >
                        <DatePicker.TableHead asChild>
                          <Thead>
                            <DatePicker.TableRow asChild>
                              <Tr>
                                {api.weekDays.map((weekDay, id) => (
                                  <DatePicker.TableHeader key={id} asChild>
                                    <Th
                                      sx={{
                                        textAlign: `center`,
                                        border: 0,
                                        fontWeight: `semibold`,
                                        color: `td`,
                                        fontSize: `sm`,
                                      }}
                                    >
                                      {weekDay.short.substring(0, 2)}
                                    </Th>
                                  </DatePicker.TableHeader>
                                ))}
                              </Tr>
                            </DatePicker.TableRow>
                          </Thead>
                        </DatePicker.TableHead>

                        <DatePicker.TableBody asChild>
                          <Tbody>
                            {api.weeks.map((week, id) => (
                              <DatePicker.TableRow key={id} asChild>
                                <Tr>
                                  {week.map((day, id) => {
                                    const disabled =
                                      api.visibleRange.start.compare(day) > 0 || api.visibleRange.end.compare(day) < 0;

                                    const current = api.value?.[0] ? api.value[0].compare(day) === 0 : false;
                                    return (
                                      <DatePicker.TableCell key={id} value={day} asChild>
                                        <Td sx={{textAlign: `center`, border: 0, padding: `2`}}>
                                          <DatePicker.TableCellTrigger style={{width: `100%`}} asChild>
                                            <Text
                                              sx={{
                                                paddingY: `4`,
                                                color: disabled ? `blackAlpha.400` : undefined,
                                                fontSize: `sm`,
                                                fontWeight: `semibold`,
                                                letterSpacing: `-1px`,
                                                backgroundColor: current ? `blackAlpha.300` : undefined,
                                                borderRadius: current ? `100%` : `sm`,
                                                cursor: disabled ? `default` : `pointer`,
                                                _hover: {
                                                  backgroundColor: current || disabled ? undefined : `blackAlpha.100`,
                                                },
                                              }}
                                            >
                                              {day.day}
                                            </Text>
                                          </DatePicker.TableCellTrigger>
                                        </Td>
                                      </DatePicker.TableCell>
                                    );
                                  })}
                                </Tr>
                              </DatePicker.TableRow>
                            ))}
                          </Tbody>
                        </DatePicker.TableBody>
                      </Table>
                    </DatePicker.Table>
                  </>
                )}
              </DatePicker.Context>
            </DatePicker.View>
          </Box>
        </DatePicker.Content>
      </DatePicker.Positioner>
    </DatePicker.Root>
  );
});

export type {Props as PopoverDatePickerProps};
